import React, { useCallback, useEffect, useRef, useState } from "react";
import { getAuthToken } from "lib/providers";
import { fetchWithMediaCache } from "lib/mediaCache";
import "./style.scss";

interface Props {
  src: string;
  duration?: number;
}

function VideoMessagePlayer({ src, duration }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loadError, setLoadError] = useState(false);

  // Fetch video with auth headers and cache via Cache API
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getAuthToken();
        if (cancelled) return;
        const url = await fetchWithMediaCache(src, token ? { Authorization: token } : {}, "video/webm");
        if (!cancelled) setBlobUrl(url);
      } catch (e) {
        console.error("Failed to load video message:", e);
        if (!cancelled) setLoadError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [src]);

  // Cleanup blob URL on unmount or when blobUrl changes (delayed to let browser finish pending loads)
  useEffect(() => {
    return () => {
      if (blobUrl) setTimeout(() => URL.revokeObjectURL(blobUrl), 3000);
    };
  }, [blobUrl]);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (playing) {
      video.pause();
      setPlaying(false);
    } else {
      video.play().then(() => setPlaying(true)).catch(() => {});
    }
  }, [playing]);

  const handleEnded = useCallback(() => {
    setPlaying(false);
  }, []);

  const formatTime = (secs: number) => {
    const s = Math.round(secs);
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  if (loadError) {
    return <div className="video-message-player __error">Video unavailable</div>;
  }

  return (
    <div className="video-message-player" onClick={togglePlay}>
      {blobUrl && (
        <video
          ref={videoRef}
          src={blobUrl}
          className="video-message-player__video"
          loop={false}
          playsInline
          preload="metadata"
          onEnded={handleEnded}
        />
      )}
      {!playing && (
        <div className="video-message-player__play-overlay">
          <svg viewBox="0 0 48 48" width="40" height="40">
            <circle cx="24" cy="24" r="22" fill="rgba(0,0,0,0.45)" />
            <path d="M18 14v20l16-10z" fill="#fff" />
          </svg>
        </div>
      )}
      {duration && (
        <span className="video-message-player__duration">{formatTime(duration)}</span>
      )}
    </div>
  );
}

export default React.memo(VideoMessagePlayer);
